import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";

export default function ContactForm() {
  const { site } = useSite();
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const services = (site?.services || []).map((s) => s.title);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="contact-section"
      id="contact"
      className="bg-white py-20 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left info */}
          <div className="lg:col-span-5 bg-neutral-950 text-white p-10 lg:p-12">
            <span className="overline text-[#DC2626]">Get In Touch</span>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-bold uppercase leading-tight tracking-tight">
              Free <span className="text-[#DC2626]">quote</span>. No obligation.
            </h2>
            <p className="mt-4 text-neutral-300">
              Tell us about your project. We usually respond within one business day.
              Or just pick up the phone — we love to talk shop.
            </p>

            <div className="mt-10 space-y-6">
              <a
                data-testid="contact-phone"
                href={`tel:${site?.phone_raw || "+12249669352"}`}
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 bg-[#DC2626] flex items-center justify-center flex-shrink-0">
                  <Phone size={20} className="text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-400">Call or Text</div>
                  <div className="font-heading text-xl font-bold group-hover:text-[#DC2626] transition-colors">
                    {site?.phone || "(224) 966-9352"}
                  </div>
                </div>
              </a>

              <a
                data-testid="contact-email"
                href={`mailto:${site?.email || "info@jscompleteremodeling.com"}`}
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 bg-[#DC2626] flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-400">Email</div>
                  <div className="font-heading text-lg font-semibold group-hover:text-[#DC2626] transition-colors break-all">
                    {site?.email}
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#DC2626] flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-400">Service Area</div>
                  <div className="font-heading text-lg font-semibold">{site?.service_area}</div>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-neutral-800">
              <p className="text-xs uppercase tracking-widest text-neutral-400 mb-2">
                Business hours
              </p>
              <p className="text-white">Mon – Sat: 7:00 AM – 7:00 PM</p>
              <p className="text-neutral-400 text-sm">Sun: By appointment</p>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            {done ? (
              <div
                data-testid="contact-success"
                className="bg-neutral-50 border-2 border-[#DC2626] p-10 lg:p-14 flex flex-col items-center text-center h-full justify-center"
              >
                <CheckCircle2 size={64} className="text-[#DC2626] mb-6" strokeWidth={2} />
                <h3 className="font-heading text-3xl font-bold uppercase">Message received</h3>
                <p className="text-neutral-600 mt-3 max-w-md">
                  Thanks! A team member will reach out shortly. For fastest service,
                  give us a call at{" "}
                  <a href={`tel:${site?.phone_raw}`} className="text-[#DC2626] font-semibold">
                    {site?.phone}
                  </a>
                  .
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setDone(false)}
                  className="mt-8 btn-secondary-cta"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-neutral-50 p-8 lg:p-12 border border-neutral-200 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-heading uppercase tracking-widest text-neutral-700 mb-2 block">
                      Name *
                    </label>
                    <input
                      data-testid="form-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={update("name")}
                      className="w-full px-4 py-3 border-2 border-neutral-200 bg-white focus:border-[#DC2626] focus:outline-none transition-colors"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-heading uppercase tracking-widest text-neutral-700 mb-2 block">
                      Phone *
                    </label>
                    <input
                      data-testid="form-phone"
                      type="tel"
                      required
                      value={form.phone}
                      onChange={update("phone")}
                      className="w-full px-4 py-3 border-2 border-neutral-200 bg-white focus:border-[#DC2626] focus:outline-none transition-colors"
                      placeholder="(555) 555-5555"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-heading uppercase tracking-widest text-neutral-700 mb-2 block">
                    Email
                  </label>
                  <input
                    data-testid="form-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="w-full px-4 py-3 border-2 border-neutral-200 bg-white focus:border-[#DC2626] focus:outline-none transition-colors"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label className="text-xs font-heading uppercase tracking-widest text-neutral-700 mb-2 block">
                    Service Interested In
                  </label>
                  <select
                    data-testid="form-service"
                    value={form.service}
                    onChange={update("service")}
                    className="w-full px-4 py-3 border-2 border-neutral-200 bg-white focus:border-[#DC2626] focus:outline-none transition-colors"
                  >
                    <option value="">Select a service…</option>
                    {services.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                    <option value="Other">Other / Not sure</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-heading uppercase tracking-widest text-neutral-700 mb-2 block">
                    Project Details
                  </label>
                  <textarea
                    data-testid="form-message"
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    className="w-full px-4 py-3 border-2 border-neutral-200 bg-white focus:border-[#DC2626] focus:outline-none transition-colors resize-none"
                    placeholder="Tell us about your project — timeline, budget, ideas…"
                  />
                </div>

                <button
                  data-testid="form-submit"
                  type="submit"
                  disabled={submitting}
                  className="btn-primary-cta w-full sm:w-auto justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending…" : "Send Request"}
                  <Send size={16} strokeWidth={2.5} />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
